/**
 * @author        matt@zumper.com (Matthieu Devlin)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Similar Listings Thunk
 */

import polyline from '@mapbox/polyline'
import { getAPIFriendlyFilters, selectFilters } from '@zcode-ui/filters'
import {
  selectRelatedPropertiesEntity as selectAddressEntity,
  selectEntityGroupId as selectAddressEntityGroupId,
} from '@zcode-ui/perm-address'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { Origin } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import * as ListC from '@zcode/nr/es/shared/constants/list'
import {
  clearSimilarListables,
  doneFetching,
  errorFetching,
  receiveSimilarListables,
  startFetching,
} from '@zcode/nr/es/shared/modules/similarListings/actions'
import {
  selectCurrentKey,
  selectFetchingKey,
  selectFetchingPromise,
} from '@zcode/nr/es/shared/modules/similarListings/selectors'
import { getSimilarListingFilters } from '@zcode/nr/es/shared/utils'
import { type ExtraThunkArg, SimilarPropertiesSource, type State } from '@zcode/state-type'
import type { Location } from 'history'
import type { Dispatch } from 'redux'
import { stateBoundingBoxes } from '../../../constants/stateBoundingBoxes'
import { isSimilarListingsExperiment } from '../../../features/RelatedProperties/hooks/isSimilarListingExperiment'
import * as Inspectors from '../../../inspectors/index'
import { getFavorited, getMessaged } from '../../../inspectors/index'
import { logger } from '../../../logger'
import { loadMoreSimilarListings } from '../../../utils/similarListings'
import {
  selectEntity as selectDetailEntity,
  selectEntityGroupId as selectDetailEntityGroupId,
} from '../../detail/modules/entity/selectors/index'
import type { GetState } from '../../types'

export enum RecommendationAlgo {
  ACTIVE = 'A',
  SIMILAR_LISTINGS_RANKED = 'B',
  SIMILAR_LISTINGS_CONTROL = 'C',
}

export const getEntityQueryKey = (
  state: State,
  isMessageableOnly: boolean | undefined,
  origin?: string
) => {
  const { listings: favoritedListings, buildings: favoritedBuildings } = getFavorited(state)
  const { listings: messagedListings, buildings: messagedBuildings } = getMessaged(state)

  const baseFilters: {
    buildingIds: number[]
    floorplans?: 0 | 1
    limit?: number
    messageable?: true
    listingIds: number[]
  } = getSimilarListingFilters({
    favoritedListings,
    favoritedBuildings,
    messagedListings,
    messagedBuildings,
  })
  // return only listings that are messageable -
  // need to be able to set this for related properties multi messaging
  if (isMessageableOnly) {
    baseFilters.messageable = true
  }
  const filters = Object.assign(
    getAPIFriendlyFilters({ filters: selectFilters(state) }),
    baseFilters
  )

  const query = {
    algo: RecommendationAlgo.SIMILAR_LISTINGS_CONTROL,
    filters,
    group_id:
      origin === Origin.ADDRESS_MM_RELATED_PROPERTIES
        ? selectAddressEntityGroupId(state)
        : selectDetailEntityGroupId(state),
    user_weighting: 0,
    limit: 12,
  }
  const key = JSON.stringify(query)
  return { baseFilters, key, query }
}

export const fetchSimilarListings =
  (isMessageableOnly?: boolean, origin?: string, location?: Location) =>
  async (dispatch: Dispatch, getState: GetState, { optimizely }: ExtraThunkArg) => {
    const state = getState()
    const entity =
      origin === Origin.ADDRESS_MM_RELATED_PROPERTIES
        ? selectAddressEntity(state)
        : selectDetailEntity(state)
    const isMobile = selectIsMobile(state)
    const filters = selectFilters(state)

    if (entity) {
      const { baseFilters, key, query } = getEntityQueryKey(state, isMessageableOnly, origin)
      const fetchingKey = selectFetchingKey(state)
      const currentKey = selectCurrentKey(state)

      if (currentKey !== key) {
        if (fetchingKey === key) {
          await selectFetchingPromise(state)
        } else {
          dispatch(startFetching(key))
          try {
            let listables = await (isSimilarListingsExperiment(optimizely, location)
              ? loadMoreSimilarListings({
                  entity,
                  favorited: Inspectors.getFavorited(state),
                  messaged: Inspectors.getMessaged(state),
                  filters,
                  forMultiMessage: true,
                  limit: ListC.SIMILAR_LISTABLES_API_LIMIT,
                })
              : Api.similarListings.query(query))
            if (entity.isBuilding) {
              const related = await Api.relatedListings.query({ building_id: entity.buildingId })
              if (related?.length > 0) {
                const limit = isMobile ? 2 : 4
                const relatedToAdd = related?.slice?.(0, limit) || []
                listables = relatedToAdd.concat(listables)
              }
            }
            let source = SimilarPropertiesSource.RELATED
            if (listables.length < 2) {
              if (entity.isBuilding) {
                baseFilters.buildingIds.push(entity.buildingId * -1)
              } else {
                baseFilters.listingIds.push(entity.listingId * -1)
              }
              baseFilters.floorplans = entity.isBuilding ? 1 : 0
              baseFilters.limit = 8
              listables = (
                await Api.listables.query({
                  ...baseFilters,
                  url: entity.cityState,
                })
              ).listables
              source = SimilarPropertiesSource.CITY
            }
            if (listables.length < 2 && entity.state && entity.state in stateBoundingBoxes) {
              // The listables API requires that we pass some kind of "geo" filter.
              // url (city_state), minLat+maxLat+minLng+maxLng (box), or polygons are acceptable;
              // states // (by itself) doesn't meet the requirement
              //
              // BE verifies that the bounding box isn't "too big" (more than 2 degrees by 2 degrees)
              // and will shrink it to that size
              //
              // That's why we use polygon instead
              const [minLng, minLat, maxLng, maxLat] =
                stateBoundingBoxes[entity.state as keyof typeof stateBoundingBoxes]
              listables = (
                await Api.listables.query({
                  ...baseFilters,
                  polygons: [
                    polyline.encode([
                      [minLat, minLng],
                      [minLat, maxLng],
                      [maxLat, maxLng],
                      [maxLat, minLng],
                      [minLat, minLng],
                    ]),
                  ],
                  states: [entity.state],
                })
              ).listables
              source = SimilarPropertiesSource.STATE
            }
            const fetchingKey = selectFetchingKey(getState())

            if (fetchingKey === key) {
              dispatch(doneFetching())
              dispatch(
                receiveSimilarListables({
                  groupId:
                    origin === Origin.ADDRESS_MM_RELATED_PROPERTIES
                      ? selectAddressEntityGroupId(state)
                      : selectDetailEntityGroupId(state),
                  key,
                  listables,
                  source,
                })
              )
            } else {
              dispatch(clearSimilarListables())
            }
          } catch (err) {
            logger.error('Failed to fetch similar listings', err)
            dispatch(clearSimilarListables())
            dispatch(errorFetching())
          }
        }
      }
    }
  }
